import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { decompress } from "compress-json";
import Dialog, { useDialog, useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import Alert from "@imminently/decisively-ui/Alert";
import Icon from "@imminently/decisively-ui/Icon";
import { getSanitizedFields } from "@packages/commons";
import { projectService } from "@/app/providers/services";
import { ExportSanitizationAlert } from "@/components/ExportSanitizationAlert";
import { useNotification } from "@/common/notifications";
import { useRouting } from "@/hooks/use-routing";
import { setProject, useScope } from "@/redux/scope";
import { type FileData, FileUpload } from "@/components/forms/hookForm/FileUpload";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { useTranslation } from "react-i18next";

const parseProject = (content: any) => {
  const parsed = JSON.parse(content);
  if (!parsed.compressed) throw new Error("Invalid export file");

  const decompressedValue = decompress(parsed.compressed);

  if (!decompressedValue.release) throw new Error("Invalid export file");
  const sanitizedFields = getSanitizedFields(decompressedValue);

  // Release export uploaded in a project import slot — warn but don't block file selection
  if (!decompressedValue.model) {
    return { ...parsed, _isReleaseExport: true, sanitizedFields };
  }

  return { ...parsed, sanitizedFields };
};

interface ImportProjectModalProps {
  close: () => void;
}

export const ImportProjectModal = ({ close }: ImportProjectModalProps) => {
  const { t } = useTranslation();
  const dialogContext = useDialogContext();
  const { workspace } = useScope();
  const dispatch = useDispatch();
  const notify = useNotification();
  const { goToPage } = useRouting();

  const { mutateAsync, isLoading } = projectService.usePostCustom("import");

  const onSubmit = async (data: { file: FileData | null; convertGuids?: boolean }) => {
    if (!data.file) {
      notify.error("No file selected");
      return;
    }

    try {
      const res = await mutateAsync({
        workspace,
        payload: data.file.data.compressed,
        convertGuids: data.convertGuids ?? false,
      });

      notify.info("Project imported successfully");
      dialogContext?.close();
      dispatch(setProject(res.id, { resetRelease: true }));
      goToPage("/build");
    } catch (e) {
      notify.error("Failed to import project");
      console.error(e);
    }
  };

  return (
    <Dialog.Content className="flex flex-col">
      <DialogHeader title={t("projects.import_label")} description="Import a previously exported project file." />

      <Form
        defaultValues={{ file: null, convertGuids: false }}
        onSubmit={onSubmit}
      >
        {() => (
          <>
            <Dialog.Body>
              <FileUpload
                field="file"
                parseFile={parseProject}
                className="pb-4"
              />
              <FieldWatch names={["file"]}>
                {({ file }) => {
                  return  <>
                    {file?.data?._isReleaseExport ? (
                      <Alert
                        variant="warning"
                        icon={<Icon name="warning" />}
                        description="This looks like a release export. To import it into an existing project, use Import Release instead."
                      />
                    ) : null}
                    {file?.data?.sanitizedFields?.length ? (
                      <ExportSanitizationAlert fields={file.data.sanitizedFields} />
                    ) : null}
                    {file && !file?.data?._isReleaseExport ? (
                      <SwitchField
                        name="convertGuids"
                        label={t("projects.convert_guids")}
                        helperText={t("projects.convert_guids_description")}
                      />
                    ) : null}
                  </>
                }}
              </FieldWatch>
            </Dialog.Body>

            <FieldWatch names={["file"]}>
              {({ file }) =>
                file ? (
                  <Dialog.Actions>
                    <Dialog.Actions.CancelButton label={t("cancel")}>{t("cancel")}</Dialog.Actions.CancelButton>
                    <Dialog.Actions.PrimaryButton
                      type="submit"
                      loading={isLoading}
                      disabled={isLoading || !!file.data?._isReleaseExport}
                      label={t("projects.import_label")}
                    >
                      {t("projects.import_label")}
                    </Dialog.Actions.PrimaryButton>
                  </Dialog.Actions>
                ) : null
              }
            </FieldWatch>
          </>
        )}
      </Form>
    </Dialog.Content>
  );
};

export const useImportProjectModal = () => {
  const { pushDialog } = useDialog();

  return useCallback(() => {
    pushDialog({ layer: WORKSPACE_LAYER, options: { intent: "form" }, component: ImportProjectModal });
  }, [pushDialog]);
};
